import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTutorial } from '../../context/TutorialContext';
import { useMobileNative } from '../../hooks/useMobileNative';
import { onboardingTutorial } from '../../config/tutorials';

interface MobileBottomNavProps {
  onTakePhoto: () => void;
  onOpenAI: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({
  onTakePhoto,
  onOpenAI
}) => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { startTutorial } = useTutorial();
  const { vibrate, safeAreaInsets } = useMobileNative();
  const [activeButton, setActiveButton] = useState<string | null>(null);

  const handleHelpClick = () => {
    startTutorial(onboardingTutorial);
    vibrate(30);
  };

  if (!isAuthenticated) {
    return null;
  }

  const handleButtonPress = (action: () => void, buttonId: string) => {
    setActiveButton(buttonId);
    vibrate(30); // Light haptic feedback
    action();
    // Reset active state after animation
    setTimeout(() => setActiveButton(null), 150);
  };

  const navigationItems = [
    {
      name: 'Listings',
      href: '/marketplace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
    },
  ];

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      style={{ 
        paddingBottom: Math.max(safeAreaInsets.bottom, 8), 
        paddingLeft: safeAreaInsets.left,
        paddingRight: safeAreaInsets.right,
      }}
    >
      {/* Background blur overlay */}
      <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-t border-slate-200/60 dark:border-slate-700/60"></div>
      
      {/* Navigation content */}
      <div className="relative px-2 py-2">
        <div className="flex items-center justify-around">
          
          {/* Navigation Items */}
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200 tap-target no-select
                  ${isActive 
                    ? 'text-primary-600 dark:text-primary-400 bg-primary-50/50 dark:bg-primary-900/20' 
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 active:bg-slate-100 dark:active:bg-slate-800'
                  }
                `}
                onClick={() => vibrate(30)}
              >
                <div className={`
                  w-6 h-6 flex items-center justify-center mb-1 transition-colors duration-200
                  ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}
                `}>
                  {item.icon}
                </div>
                <span className="text-xs font-medium">
                  {item.name}
                </span>
              </Link>
            );
          })}

          {/* Take Photo Button */}
          <button
            onClick={() => handleButtonPress(onTakePhoto, 'camera')}
            className={`
              flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200 tap-target-large no-select gpu-accelerated
              bg-primary-500 text-white shadow-lg hover:shadow-xl active:scale-95
              ${activeButton === 'camera' ? 'scale-95' : ''}
            `}
          >
            <div className="w-8 h-8 flex items-center justify-center mb-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="text-xs font-medium">
              Photo
            </span>
          </button>

          {/* Help Button */}
          <button
            onClick={handleHelpClick}
            className={`
              flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200 tap-target no-select
              text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 
              active:bg-slate-100 dark:active:bg-slate-800
            `}
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <span className="text-xs font-medium">
              Help
            </span>
          </button>

          {/* AI Assistant Button */}
          <button
            onClick={() => handleButtonPress(onOpenAI, 'ai')}
            className={`
              flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200 tap-target no-select
              text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 
              active:bg-slate-100 dark:active:bg-slate-800
              ${activeButton === 'ai' ? 'bg-slate-100 dark:bg-slate-800 scale-95' : ''}
            `}
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <span className="text-xs font-medium">
              AI
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};